/**
 * Ministry Equipment Service
 * Handles ministry equipment management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MinistryEquipmentService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MinistryEquipmentService.DEFAULT_SERVICE_PATH,
            basePath: MinistryEquipmentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all ministry equipment with optional filters
     * @param isActive - Optional filter for active/inactive equipment
     * @param ministryId - Optional filter by ministry ID
     * @returns Array of ministry equipment
     */
    async getAll(isActive, ministryId) {
        const response = await this.getAllRaw(isActive, ministryId);
        return response.data ?? [];
    }
    /**
     * Get all ministry equipment with optional filters (raw response)
     * @param isActive - Optional filter for active/inactive equipment
     * @param ministryId - Optional filter by ministry ID
     * @returns Raw response with array of ministry equipment
     */
    getAllRaw(isActive, ministryId) {
        const params = {};
        if (isActive !== undefined)
            params.isActive = isActive;
        if (ministryId !== undefined)
            params.ministryId = ministryId;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get ministry equipment by ID
     * @param id - Ministry equipment ID
     * @returns Ministry equipment details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ministry equipment with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry equipment by ID (raw response)
     * @param id - Ministry equipment ID
     * @returns Raw response with ministry equipment details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new ministry equipment
     * @param data - Ministry equipment creation data
     * @returns Created ministry equipment
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create ministry equipment');
        }
        return response.data;
    }
    /**
     * Create a new ministry equipment (raw response)
     * @param data - Ministry equipment creation data
     * @returns Raw response with created ministry equipment
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing ministry equipment
     * @param id - Ministry equipment ID
     * @param data - Ministry equipment update data
     * @returns Updated ministry equipment
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update ministry equipment with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing ministry equipment (raw response)
     * @param id - Ministry equipment ID
     * @param data - Ministry equipment update data
     * @returns Raw response with updated ministry equipment
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a ministry equipment
     * @param id - Ministry equipment ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a ministry equipment (raw response)
     * @param id - Ministry equipment ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate ministry equipment cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate ministry equipment cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MinistryEquipmentService.DEFAULT_SERVICE_PATH = 'departments';
MinistryEquipmentService.BASE_PATH = 'api/ministryequipment';
//# sourceMappingURL=MinistryEquipmentService.js.map